<template>
  <div v-if="prop.show" class="field-conversion">
    <FieldLabel v-if="showFieldHelp" :label="item.name" :help="fieldHelp" :input-id="fieldInputId" :field-id="item.id" :modal-key="modalKey" />
    <div class="field-conversion__row">
      <v-text-field
        ref="input"
        :id="fieldInputId"
        :value="displayValue"
        :name="item.value"
        :label="showFieldHelp ? undefined : item.name"
        :aria-label="item.name"
        type="text"
        inputmode="decimal"
        :suffix="displayUnit.symbol"
        :rules="inputRules"
        :outlined="prop.outlined"
        :filled="prop.filled"
        :solo="prop.solo"
        :hint="prop.description"
        :placeholder="prop.placeholder"
        :persistent-hint="prop.persistenthint"
        :disabled="prop.disabled"
        :readonly="prop.readonly"
        :clearable="prop.clearable"
        :color="prop.coloronselect"
        :class="['field-conversion__input', prop.class]"
        :prepend-icon="prop.iconlr === 'prepend' && prop.iconinout === 'out' ? prop.iconmdi : ''"
        :prepend-inner-icon="prop.iconlr === 'prepend' && prop.iconinout !== 'out' ? prop.iconmdi : ''"
        :append-icon="prop.iconlr === 'append' && prop.iconinout !== 'out' ? prop.iconmdi : ''"
        :append-outer-icon="prop.iconlr === 'append' && prop.iconinout === 'out' ? prop.iconmdi : ''"
        @input="onInput"
        @focus="editing = true"
        @blur="onBlur"
        @keydown.enter="onEnter"
      />
      <v-btn-toggle
        v-if="settings.allowUnitSwitch"
        :value="selectedUnit"
        mandatory
        class="field-conversion__units"
        color="primary"
        :aria-label="'Unité de ' + item.name"
        @change="selectUnit"
      >
        <v-btn v-for="unit in selectableUnits" :key="unit.value" :value="unit.value" small
          :disabled="Boolean(prop.disabled || prop.readonly || conversionError)"
          :title="unit.text">
          {{ unit.symbol }}
        </v-btn>
      </v-btn-toggle>
    </div>
  </div>
</template>

<script>
import mixinFields from '../../mixins/Fields'
import mixinConditionsFields from '../../mixins/conditionsFields'
import { normalizeConversionSettings, getConversionUnits, toStorageValue, fromStorageValue } from '../../services/fieldConversions'

export default {
  name: 'ConversionField',
  mixins: [mixinFields, mixinConditionsFields],
  data() {
    return { items: [], originalItems: [], displayValue: '', selectedUnit: null, conversionError: '', editing: false, pendingValue: undefined, hasPendingValue: false }
  },
  computed: {
    settings() { return normalizeConversionSettings((this.item.otherparams || {}).conversion) },
    settingsSignature() { return JSON.stringify(this.settings) },
    displayUnit() {
      return getConversionUnits(this.settings.family).find(unit => unit.value === (this.selectedUnit || this.settings.defaultUnit))
        || getConversionUnits(this.settings.family)[0]
    },
    selectableUnits() { return getConversionUnits(this.settings.family).filter(unit => this.settings.allowedUnits.includes(unit.value)) },
    inputRules() { return [...(this.prop.modelRules || []), () => this.conversionError || true] }
  },
  watch: {
    valField(value) {
      if (this.hasPendingValue && this.sameValue(value, this.pendingValue)) {
        this.hasPendingValue = false
        if (this.editing) return
      }
      this.hasPendingValue = false
      this.syncDisplay(value)
    },
    settingsSignature(value, previous) {
      const old = previous ? JSON.parse(previous) : null
      if (!old || old.family !== this.settings.family || old.storageUnit !== this.settings.storageUnit
        || old.defaultUnit !== this.settings.defaultUnit || !this.settings.allowUnitSwitch
        || !this.settings.allowedUnits.includes(this.selectedUnit)) this.selectedUnit = this.settings.defaultUnit
      this.syncDisplay(this.model)
    }
  },
  mounted() {
    // Les mixins et les directives conservent model dans l'unité stockée.
    this.items = []
    this.selectedUnit = this.settings.defaultUnit
    this.syncDisplay(this.valField)
  },
  methods: {
    sameValue(left, right) {
      if (left === right) return true
      return left !== null && left !== undefined && left !== '' && right !== null && right !== undefined && right !== ''
        && Number(left) === Number(right)
    },
    // Une mise à jour externe n'est jamais reconvertie ni réémise au formulaire.
    syncDisplay(value) {
      const converted = fromStorageValue(value, this.settings, this.displayUnit.value)
      this.displayValue = converted.display
      this.conversionError = converted.error
      this.validateInput()
    },
    validateInput() {
      this.$nextTick(() => { if (this.$refs.input) this.$refs.input.validate() })
    },
    // Seule une saisie valide peut remplacer la valeur canonique du formulaire.
    onInput(value) {
      if (this.prop.disabled || this.prop.readonly) return
      this.editing = true
      this.displayValue = value === null || value === undefined ? '' : String(value)
      const converted = toStorageValue(this.displayValue, this.settings, this.displayUnit.value)
      this.conversionError = converted.error
      if (converted.valid && !this.sameValue(converted.value, this.model)) {
        this.pendingValue = converted.value
        this.hasPendingValue = true
        this.model = converted.value
        this.keyup(this.item, converted.value, false)
      }
      this.validateInput()
    },
    // Les boutons ne changent que la présentation de la quantité courante.
    selectUnit(unit) {
      if (this.prop.disabled || this.prop.readonly || this.conversionError || !this.settings.allowUnitSwitch) return
      if (!this.settings.allowedUnits.includes(unit)) return
      this.selectedUnit = unit
      this.syncDisplay(this.model)
    },
    onBlur() {
      this.editing = false
      if (!this.conversionError) this.syncDisplay(this.model)
    },
    onEnter(event) {
      const invalid = this.conversionError || this.prop.disabled || this.prop.readonly
        || this.inputRules.some(rule => typeof rule === 'function' && rule(this.displayValue) !== true)
      if (invalid) {
        if (event) {
          event.preventDefault()
          event.stopPropagation()
        }
      } else this.keyup(this.item, this.model, true)
      this.validateInput()
    }
  }
}
</script>

<style scoped>
.field-conversion__row { display: flex; align-items: flex-start; gap: 8px; width: 100%; min-width: 0; }
.field-conversion__input { flex: 1 1 auto; min-width: 0; }
.field-conversion__units { flex: 0 0 auto; margin-top: 5px; }
.field-conversion__units .v-btn { min-width: 38px !important; padding: 0 8px !important; }
@media (max-width: 400px) {
  .field-conversion__row { flex-wrap: wrap; }
  .field-conversion__input { flex-basis: 100%; }
  .field-conversion__units { margin-top: -8px; margin-bottom: 10px; }
}
</style>
